<template>
  <section class="realname-form">
    <div class="identity-notice"><i class="el-icon-success" aria-hidden="true"></i><div><strong>信息安全说明</strong><p>身份信息仅用于参赛资格核验、证书生成与赛事管理，请确保填写内容与证件一致。</p></div></div>
    <template v-if="hasNoApplication || editing">
      <h3>身份信息</h3>
      <p class="identity-tip">支持居民身份证及港澳台居民、出入境等有效证件</p>
      <el-form ref="form" :model="form" :rules="rules" label-position="top" @submit.native.prevent="submit">
        <div class="identity-fields">
          <el-form-item label="真实姓名" prop="realName"><el-input v-model.trim="form.realName" maxlength="80" autocomplete="off" placeholder="请输入证件上的姓名" /></el-form-item>
          <el-form-item label="证件类型" prop="idType"><el-select v-model="form.idType" placeholder="请选择证件类型"><el-option v-for="type in idTypes" :key="type" :label="type" :value="type" /></el-select></el-form-item>
          <el-form-item class="identity-full" label="证件号码" prop="idNo"><el-input v-model.trim="form.idNo" maxlength="80" autocomplete="off" placeholder="请输入证件号码" /></el-form-item>
          <el-form-item label="学校 / 单位" prop="schoolName"><el-input v-model.trim="form.schoolName" maxlength="200" placeholder="请输入学校或单位" /></el-form-item>
          <el-form-item label="当前年级" prop="gradeName"><el-select v-model="form.gradeName" :disabled="!grades.length" placeholder="请选择年级"><el-option v-for="grade in grades" :key="grade.value" :label="grade.label" :value="grade.value" /></el-select></el-form-item>
        </div>
        <p v-if="gradeError" class="review-notice">年级选项暂时无法加载，请稍后重试。</p>
        <p class="review-notice">提交后可在此页面及通知中心查看认证结果。</p>
      <div class="identity-actions"><el-button v-if="allowLater" :disabled="saving" @click="$emit('later')">稍后认证</el-button><el-button v-else-if="editing" :disabled="saving" @click="cancelEditing">取消修改</el-button><el-button type="primary" native-type="submit" :disabled="!grades.length" :loading="saving">确认并提交认证</el-button></div>
      </el-form>
    </template>
    <div v-else class="identity-status">
      <i :class="verified ? 'el-icon-circle-check' : 'el-icon-info'" aria-hidden="true"></i>
      <h3>{{statusText}}</h3>
      <p v-if="verified">您已完成实名认证，无需重复申请。</p>
      <p v-else-if="profile.realnameStatus==='REJECTED'">您的申请未通过审核，请修改身份信息后重新申请。</p>
      <p v-else>您已有实名认证申请，登录后不会重复弹出申请窗口。</p>
      <p v-if="profile.rejectReason" class="rejection-reason">驳回原因：{{profile.rejectReason}}</p>
      <dl v-if="profile.realName || profile.idNoMasked"><div v-if="profile.realName"><dt>真实姓名</dt><dd>{{profile.realName}}</dd></div><div v-if="profile.idNoMasked"><dt>证件号码</dt><dd>{{profile.idNoMasked}}</dd></div></dl>
      <div v-if="profile.submittedTime || (verified && profile.reviewedTime)" class="identity-times">
        <p v-if="profile.submittedTime">申请时间：{{formatDateTime(profile.submittedTime)}}</p>
        <p v-if="verified && profile.reviewedTime">审核通过时间：{{formatDateTime(profile.reviewedTime)}}</p>
      </div>
      <el-button v-if="profile.realnameStatus==='REJECTED'" type="primary" :loading="editLoading" @click="startEditing">修改并重新申请</el-button>
    </div>
  </section>
</template>

<script>
import { hasNoRealnameApplication, realnameStatusText } from '@/utils/realname'

export default {
  props: { profile: { type: Object, default: () => ({}) }, allowLater: Boolean },
  data() {
    const required = message => ({ required: true, message, trigger: 'blur' })
    return {
      saving: false,
      editing: false,
      editLoading: false,
      gradeError: false,
      form: { realName: this.profile.realName || '', idType: this.profile.idType || '居民身份证', idNo: '', schoolName: this.profile.schoolName || '', gradeName: this.profile.gradeName || '' },
      idTypes: ['居民身份证', '港澳居民来往内地通行证', '台湾居民来往大陆通行证', '港澳台居民居住证', '护照', '其他有效证件'],
      grades: [],
      rules: { realName: [required('请输入真实姓名')], idType: [required('请选择证件类型')], idNo: [required('请输入证件号码')], schoolName: [required('请输入学校或单位')], gradeName: [required('请选择当前年级')] },
    }
  },
  computed: {
    hasNoApplication() { return hasNoRealnameApplication(this.profile) },
    statusText() { return realnameStatusText(this.profile) },
    verified() { return ['VERIFIED', '已认证'].includes(this.profile.realnameStatus) },
  },
  created() { this.loadGrades() },
  methods: {
    formatDateTime(value) {
      if (!value) return ''
      const matched = String(value).match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/)
      return matched ? `${matched[1]} ${matched[2]}` : String(value)
    },
    async loadGrades() {
      try { const response = await this.$api('/api/website/options/edu_student_grade', 'get'); this.grades = response.data || []; this.gradeError = !this.grades.length }
      catch (error) { this.grades = []; this.gradeError = true }
    },
    async startEditing() {
      if (this.editLoading) return
      this.editLoading = true
      try {
        const response = await this.$api('/api/website/student/realname/rejected-edit', 'get')
        const application = response.data || {}
        this.form = {
          realName: application.realName || '',
          idType: application.idType || '居民身份证',
          idNo: application.idNo || '',
          schoolName: application.schoolName || '',
          gradeName: application.gradeName || '',
        }
        this.editing = true
        this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
      } catch (error) { /* 请求封装负责显示错误，失败时不进入编辑状态。 */ }
      finally { this.editLoading = false }
    },
    cancelEditing() {
      this.form.idNo = ''
      this.editing = false
    },
    submit() {
      if (this.saving || !this.grades.some(grade => grade.value === this.form.gradeName)) return
      this.$refs.form.validate(async valid => {
        if (!valid || this.saving) return
        this.saving = true
        try {
          const response = await this.$api('/api/website/student/realname', 'post', this.form)
          this.form.idNo = ''
          this.editing = false
          this.$emit('submitted', response.data || {})
          window.dispatchEvent(new Event('front-realname-updated'))
          this.$message.success(response.data && response.data.realnameStatus === 'VERIFIED' ? '实名认证成功' : '实名认证申请已提交，请等待后台审核')
        } catch (error) { /* 请求封装负责显示错误，失败时不更新申请状态。 */ }
        finally { this.saving = false }
      })
    },
  },
}
</script>

<style scoped lang="less">
.realname-form{color:#28364d}.identity-notice{display:flex;align-items:center;gap:14px;padding:17px;border:1px solid #dce9ff;border-radius:9px;background:#eef5ff}.identity-notice>i{flex:none;color:#2867e8;font-size:30px}.identity-notice strong{font-size:14px}.identity-notice p{margin:5px 0 0;color:#7e8da4;font-size:12px;line-height:1.7}.realname-form h3{margin:25px 0 8px;font-size:18px}.identity-tip{margin:0 0 23px;color:#8794a9;font-size:12px;line-height:1.7}.identity-fields{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:0 20px}.identity-full{grid-column:1/-1}.identity-fields /deep/ .el-form-item{margin-bottom:20px}.identity-fields /deep/ .el-form-item__label{padding-bottom:7px;line-height:22px;font-size:13px}.identity-fields /deep/ .el-input__inner{height:42px;border-color:#e0e7f2;border-radius:8px;font-size:14px}.identity-fields .el-select{width:100%}.service-notice{margin:4px 0 20px}.identity-actions{display:flex;justify-content:flex-end;gap:10px;padding-top:19px;border-top:1px solid #edf1f7}.identity-actions /deep/ .el-button{margin-left:0;border-radius:7px}.identity-actions /deep/ .el-button--primary{background:#2867e8;border-color:#2867e8}.identity-status{text-align:center;padding:30px 0}.identity-status>i{font-size:48px;color:#2867e8}.identity-status p{color:#7e8da4;font-size:14px;line-height:1.7}.identity-status dl{text-align:left;max-width:400px;margin:25px auto 0}.identity-status dl div{display:flex;gap:20px;padding:12px 0;border-bottom:1px solid #edf1f7}.identity-status dt{color:#8794a9;flex:none}.identity-status dd{margin:0;overflow-wrap:anywhere}
@media(max-width:600px){.identity-fields{grid-template-columns:minmax(0,1fr)}.identity-notice{padding:13px;gap:10px}.identity-notice>i{font-size:25px}.identity-actions{flex-wrap:wrap}.identity-actions /deep/ .el-button{flex:1;padding-left:12px;padding-right:12px}}
</style>
<style scoped>
.review-notice{margin:0 0 20px;color:#8794a9;font-size:12px;line-height:1.7}.rejection-reason{padding:12px;background:#fff3f0;border-radius:8px;color:#bb5141!important;overflow-wrap:anywhere}.identity-times{margin:14px 0 0}.identity-times p{margin:5px 0}
</style>
